import {
  type CiPortalConfig,
  type MockRequest,
  type MockRoute,
  type MockRouteInput,
  type MockToken,
  type MockTokenDetail,
  type Page,
} from "../../../shared/types";
import { apiRequest } from "../../lib/api";

/** React Query keys of the mock pages. */
export const mockKeys = {
  all: ["mocks"] as const,
  list: (filters: MockListFilters) => ["mocks", "list", filters] as const,
  detail: (tokenUuid: string) => ["mocks", "detail", tokenUuid] as const,
  requests: (tokenUuid: string) => ["mocks", "requests", tokenUuid] as const,
};

/** Filters of the mock list. */
export type MockListFilters = {
  /** Free-text search */
  search: string;
  /** Only mine */
  mine: boolean;
  /** Leave out expired */
  active: boolean;
  /** Page offset */
  offset: number;
};

export const MOCK_PAGE_SIZE = 50;

/** GET /api/config */
export const fetchConfig = (): Promise<CiPortalConfig> =>
  apiRequest({ path: "/api/config" });

/** GET /api/mocks */
export const fetchMocks = (
  filters: MockListFilters,
): Promise<Page<MockToken>> =>
  apiRequest({
    path: "/api/mocks",
    query: { ...filters, limit: MOCK_PAGE_SIZE },
  });

/** GET /api/mocks/:tokenUuid */
export const fetchMock = (tokenUuid: string): Promise<MockTokenDetail> =>
  apiRequest({ path: `/api/mocks/${tokenUuid}` });

/** GET /api/mocks/:tokenUuid/requests */
export const fetchMockRequests = (
  tokenUuid: string,
): Promise<{ items: MockRequest[] }> =>
  apiRequest({
    path: `/api/mocks/${tokenUuid}/requests`,
    query: { limit: 200 },
  });

/** Body of POST /api/mocks and PATCH /api/mocks/:tokenUuid. */
export type MockTokenBody = {
  /** Label */
  label?: string | null;
  /** Days until it expires */
  expiresInDays?: number;
  /** Expiry; null never expires */
  expiresAt?: string | null;
};

/** POST /api/mocks */
export const createMock = (body: MockTokenBody): Promise<MockTokenDetail> =>
  apiRequest({ method: "POST", path: "/api/mocks", body });

/** PATCH /api/mocks/:tokenUuid */
export const updateMock = (
  tokenUuid: string,
  body: MockTokenBody,
): Promise<MockTokenDetail> =>
  apiRequest({ method: "PATCH", path: `/api/mocks/${tokenUuid}`, body });

/** DELETE /api/mocks/:tokenUuid */
export const deleteMock = (tokenUuid: string): Promise<void> =>
  apiRequest({ method: "DELETE", path: `/api/mocks/${tokenUuid}` });

/** POST or PATCH a route. */
export const saveRoute = (
  tokenUuid: string,
  routeUuid: string | null,
  body: MockRouteInput,
): Promise<MockRoute> =>
  routeUuid
    ? apiRequest({
        method: "PATCH",
        path: `/api/mocks/${tokenUuid}/routes/${routeUuid}`,
        body,
      })
    : apiRequest({
        method: "POST",
        path: `/api/mocks/${tokenUuid}/routes`,
        body,
      });

/** DELETE a route. */
export const deleteRoute = (
  tokenUuid: string,
  routeUuid: string,
): Promise<void> =>
  apiRequest({
    method: "DELETE",
    path: `/api/mocks/${tokenUuid}/routes/${routeUuid}`,
  });

/** DELETE /api/mocks/:tokenUuid/requests */
export const clearRequests = (
  tokenUuid: string,
): Promise<{ deleted: number }> =>
  apiRequest({ method: "DELETE", path: `/api/mocks/${tokenUuid}/requests` });

/** POST /api/mocks/cleanup */
export const cleanupExpired = (): Promise<{ deleted: number }> =>
  apiRequest({ method: "POST", path: "/api/mocks/cleanup" });

/** Full URL of a mock, or its path when the base URL isn't configured. */
export const mockUrl = (
  config: CiPortalConfig | undefined,
  tokenUuid: string,
  path = "",
): string => `${config?.mockBaseUrl ?? ""}/${tokenUuid}${path}`;
